import { deepgramTTSService } from "services/voice/deepgramTTSService";
import { listVisitStartOps } from "pages/visitStart/visitStartOfflineQueue";

const ALWAYS_CLEAR_LOCAL_KEYS = [
  "emailForSignIn",
  "pendingShareReturn",
  "activeCheckIn",
  "userLocation",
];

const PUBLIC_MODE_SC4_KEYS = [
  "sc4_has_consented",
  "hd_sc4_intake_locale",
  "sc4_cmr_seed_opted_in",
];

const VOXPONT_DIAGNOSTICS_KEY = "VoxPont:lastConnectDiagnostics";

const VOXPONT_KEY_SUFFIXES = [
  ":lastLanguagePair",
  ":recentLanguagePairs",
  ":instructionsDismissed",
];

const VISITSTART_OFFLINE_DB = "visitstart-offline";

function safeRemoveLocalStorageKey(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore quota / private-mode failures
  }
}

function isVoxPontPrefKey(key) {
  if (key === VOXPONT_DIAGNOSTICS_KEY) return true;
  return VOXPONT_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix));
}

/**
 * Delete IndexedDB `visitstart-offline` when present.
 * @returns {Promise<void>}
 */
export async function deleteVisitStartOfflineDb() {
  if (typeof indexedDB === "undefined") return;
  await new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(VISITSTART_OFFLINE_DB);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

/**
 * Clear browser-local traces after logout (or before forced public-mode wipe).
 *
 * Always removes a fixed set of localStorage keys and Deepgram TTS RAM cache.
 * When `publicMode` is true, also clears symptom-check prefs, VoxPont language /
 * instructions / diagnostics keys, all of sessionStorage, and IndexedDB
 * `visitstart-offline` when its ops queue is empty.
 *
 * @param {{ publicMode?: boolean }} [options]
 * @returns {Promise<{ pendingOfflineWrites: number }>}
 */
export async function clearDeviceTraces({ publicMode = false } = {}) {
  for (const key of ALWAYS_CLEAR_LOCAL_KEYS) {
    safeRemoveLocalStorageKey(key);
  }

  try {
    deepgramTTSService.clearCache();
  } catch {
    // ignore
  }

  let pendingOfflineWrites = 0;
  try {
    const ops = await listVisitStartOps();
    pendingOfflineWrites = Array.isArray(ops) ? ops.length : 0;
  } catch {
    pendingOfflineWrites = 0;
  }

  if (!publicMode) {
    return { pendingOfflineWrites };
  }

  for (const key of PUBLIC_MODE_SC4_KEYS) {
    safeRemoveLocalStorageKey(key);
  }

  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key) keys.push(key);
    }
    for (const key of keys) {
      if (isVoxPontPrefKey(key)) {
        safeRemoveLocalStorageKey(key);
      }
    }
  } catch {
    // ignore
  }

  try {
    window.sessionStorage.clear();
  } catch {
    // ignore
  }

  if (pendingOfflineWrites === 0) {
    await deleteVisitStartOfflineDb();
  }

  return { pendingOfflineWrites };
}
